import React from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import moment from 'moment';

import { ISubmission } from '../../../../store/SubmissionSlice';
import DetailViewCard, {
  IDetailViewCard,
} from '../../../../components/Card/DetailViewCard';

interface SubmissionListItemProps {
  submission: ISubmission;
  attemptNumber: string;
  onView: () => void;
}

const detailedCardDefaultProps: IDetailViewCard = {
  titleProps: {
    variant: 'subtitle2',
    fontWeight: 500,
    fontSize: '14px',
  },
  descriptionProps: {
    variant: 'subtitle2',
    fontWeight: 500,
    fontSize: '18px',
  },
  containerProps: {
    style: {
      padding: 0,
      marginBottom: 0,
      backgroundColor: 'transparent',
    },
  },
  title: '',
  description: '',
};

const containerStyles: React.CSSProperties = {
  border: '1px solid #E2E8F0',
  borderRadius: '5px',
};

const SubmissionListItem: React.FC<SubmissionListItemProps> = ({
  submission,
  attemptNumber,
  onView,
}) => {
  const submissionDateTime = moment(submission.createdAt).format(
    'YYYY-MM-DD hh:mm A'
  );

  const score =
    submission.totalScore >= 0 && submission.score >= 0
      ? `${submission.score
          .toString()
          .padStart(2, '0')} / ${submission.totalScore
          .toString()
          .padStart(2, '0')}`
      : '-';

  let timeTaken = <span>-</span>;

  if (
    submission.timerStart &&
    submission.timerEnd &&
    submission.isTimerEnabled &&
    submission.status === 'completed'
  ) {
    const _allocatedSeconds = submission.totalScore * 60;
    const _spentSeconds = (submission.timerEnd - submission.timerStart) / 1000;

    const _diff = _allocatedSeconds - _spentSeconds;
    const _diffAbs = Math.abs(_allocatedSeconds - _spentSeconds);

    const _mins = Math.abs(Math.floor(_spentSeconds / 60));
    const _secs = Math.abs(Math.floor(_spentSeconds % 60));

    const _minText = `${_mins} Min${_mins > 1 ? 's' : ''}`;
    const _secText = `${_secs} Sec${_secs > 1 ? 's' : ''}`;

    let _extraMins = 0;
    let _extraSecs = 0;
    let _extraText = '';

    if (_diff > 0 || _diff < 0) {
      _extraMins = Math.floor(_diffAbs / 60);
      _extraSecs = Math.floor(_diffAbs % 60);
      _extraText = `${_extraMins} Min${_extraMins > 1 ? 's' : ''}`;

      if (_extraSecs > 0) {
        _extraText += ` ${_extraSecs} Sec${_extraSecs > 1 ? 's' : ''}`;
      }
    }

    let status: React.ReactElement | null = null;

    if (_diff === 0) {
      status = (
        <Typography color="wraning" variant="caption">
          on time
        </Typography>
      );
    } else if (_diff > 0) {
      status = (
        <Typography color="success.main" variant="caption">
          <NorthIcon color="success" fontSize="small" sx={{ height: '15px' }} />{' '}
          {_extraText}
        </Typography>
      );
    } else if (_diff < 0) {
      status = (
        <Typography color="error.main" variant="caption">
          <SouthIcon color="error" fontSize="small" sx={{ height: '15px' }} />{' '}
          {_extraText}
        </Typography>
      );
    }

    timeTaken = (
      <>
        <span style={{ fontSize: '18px' }}>
          {_mins > 0 ? _minText : ''}
          {_secs > 0 && _mins > 0 ? ' ' : ''}
          {_secs > 0 ? _secText : ''}
          &nbsp;{status}
        </span>
      </>
    );
  }

  return (
    <Card elevation={0} style={containerStyles}>
      <CardContent style={{ padding: '16px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2} lg={2}>
            <DetailViewCard
              {...detailedCardDefaultProps}
              title="Attempt"
              description={attemptNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <DetailViewCard
              {...detailedCardDefaultProps}
              title="Submission Date & Time"
              description={submissionDateTime}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={2}>
            <DetailViewCard
              {...detailedCardDefaultProps}
              title="Score"
              description={score}
            />
          </Grid>
          <Grid item xs={12} sm={9} md={4} lg={4}>
            <DetailViewCard
              {...detailedCardDefaultProps}
              title="Time Taken"
              descriptionElement={timeTaken}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={1} lg={1}>
            <Stack
              direction="row"
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            >
              <Button
                size="small"
                variant="contained"
                color="inherit"
                style={{ fontWeight: 600, backgroundColor: '#F1F5F9' }}
                onClick={onView}
              >
                View
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SubmissionListItem;
